import React, { useRef, useState, useEffect, useContext, useMemo, useCallback } from 'react'
import lzString from 'lz-string'
import cn from 'classnames'
import { typographyStyles } from '@grammarly/badger-ui'
import { LensedAlert } from '@grammarly/http-clients-capi'
import { Document } from '@contentful/rich-text-types'
import { DoxRequest } from '@grammarly/http-clients-dox'
import { FreeUserTrialModal } from '@components/ui-lib/modal/free-user-trial-modal'
import { useIntl } from 'react-intl'

import { PageContext } from '../../../../../contexts/page-context'
import {
  useApiService,
  useBrowserService,
  useExperimentService,
  useUserService,
  useFreeLimitToTrialExperiment,
} from '../../../../../lib/hooks'
import { gnarSpec } from '../../../../../lib/tracking/gnar'
import { calculateWordCount, trimUserText } from '../../../../../lib/utils/strings'
import {
  LocalStorageKey,
  setLocalStorageItem,
  useLocalStorageItem,
  useSetLocalStorageItem,
} from '../../../../../lib/utils/local-storage'
import { QueryParams } from '../../../../../lib/utils/url'
import { navHelper } from '../../../../../lib/services/navigation'
import { ButtonColor, ButtonCta, ButtonStatus } from '../../../../ui-lib/button'
import { renderRichTextSingleLineContent } from '../../../../renderer/rich-text'

import { TextCheckerNotEnoughTextAlert } from '../not-enough-text-alert'
import { WordsLeft } from '../words-left'
import { UploadButton } from '../upload-button'
import { sampleText } from './sample-text'
import { TextCheckerOverlay } from '../overlay'
import { TextCheckStatus } from '../types'
import { ReportSummary } from '../report-summary'
import { FeatureErrorsType } from '../report-summary/types'
import { parseAlerts } from '../report-summary/helpers'
import { transformToString } from '../../../../../lib/utils/error'
import { getConfig } from '../../../../../config'
import { HelperMessage } from './helper-message'
import { Wall } from './wall'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { isPlagiarismCheckerPage } from '@lib/utils/page-rules/by-slug'
import {
  getExpOptimalUsageLimit,
  isToolsOptimalUsageLimitTestTreatment,
  UrlBasedUsageTracker,
} from '../../../../../lib/utils/tool-usage'
import { trackExperimentInfo } from '@lib/tracking/google-tag-manager/events'

import styles from './form.module.scss'

const MAX_USER_INPUT_CHAR_COUNT = 10000
const editorHomeUrl = getConfig().urls.editor

const handleCheckTextSuccess = (userText: string, alerts: LensedAlert[]) => {
  // this local storage item will be read and removed after navigating to Funnel
  setLocalStorageItem(LocalStorageKey.checkedText, userText)

  const queryParams: QueryParams = {
    alerts: lzString.compressToEncodedURIComponent(JSON.stringify(alerts)),
  }
  navHelper.navigateFunnelPath('/report', queryParams)
}

interface TextCheckerReportProps {
  minimumWordCount: number
  autofocus?: boolean
  buttonCtaText?: Document
  placeholderText?: string
  isFileUploadEnabled?: boolean
  scanForPlagiarism?: boolean
}

const maxSubmits = 1
const expirationPeriod = 1000 * 3600 * 20

export const TextCheckerReport: React.FC<TextCheckerReportProps> = (props) => {
  const intl = useIntl()
  const { capi, dox } = useApiService()
  const { isGDPR, getToolsOptimalUsageLimitAssignment, experimentsWithAssignedTreatment } = useExperimentService()
  const { pageId, slug } = useContext(PageContext)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [hasMounted, setHasMounted] = useState(false)
  const [userText, setUserText] = useState('')
  const [textCheckStatus, setTextCheckStatus] = useState<TextCheckStatus>('ACTIVE')
  const [wordCount, setWordCount] = useState(0)
  const [parsedTextCheckErrors, setParsedTextCheckErrors] = useState<FeatureErrorsType[]>()
  const [hasSeenWall, setHasSeenWall] = useState(false)
  const [showWall, setShowWall] = useState(false)
  const [bounceCta, setBounceCta] = useState(false)
  const hasInitialChangeRef = useRef(false)

  const { isMobileOrTablet } = useBrowserService()
  const { currentUser } = useUserService()
  const { isAnonymous, isPremium: isPremiumUser } = currentUser

  const {
    usageTracker: freeTrialUsageTracker,
    showTrialModal,
    setShowTrialModal,
    isToolsFreeUseLimitExperiment,
  } = useFreeLimitToTrialExperiment()

  const plagiarismCheckerUsageTracker = useMemo(() => {
    if (freeTrialUsageTracker) {
      return freeTrialUsageTracker
    }
    return new UrlBasedUsageTracker(undefined, false, undefined, currentUser)
  }, [currentUser, freeTrialUsageTracker])
  const countSubmitString = useLocalStorageItem(LocalStorageKey.countPlagiarismSubmitAnonymous)
  const wallExpiration = useLocalStorageItem(LocalStorageKey.expirationPlagiarismSubmitAnonymous)
  const setLocalStorageItem = useSetLocalStorageItem()

  const isForPlagiarismPlanPage = isAnonymous && isPlagiarismCheckerPage(slug) && !isGDPR

  useEffect(() => {
    if (isForPlagiarismPlanPage) {
      setLocalStorageItem(LocalStorageKey.isFromPlagiarismChecker, 'true')
    }
  }, [isForPlagiarismPlanPage, setLocalStorageItem])

  const shouldHaveVoluntaryCta = !isMobileOrTablet
  const shouldHaveFastWall = !isMobileOrTablet && isAnonymous
  const shouldHaveBouncyCtaWall = !isMobileOrTablet && isAnonymous

  const isOnePageReport = props.scanForPlagiarism
  const showUploadButton = !!props.isFileUploadEnabled && !isMobileOrTablet

  const triggerUsageWall = useCallback(() => {
    if (isToolsFreeUseLimitExperiment) {
      freeTrialUsageTracker?.checkAndTriggerLimitReached()
      return
    }
    setShowWall(true)
    setHasSeenWall(true)
  }, [isToolsFreeUseLimitExperiment, freeTrialUsageTracker])

  const redirectUserToEditor = async (text: string) => {
    const doxCreateRequest = DoxRequest.Create.textFull({
      doc: { ops: [{ insert: text }] },
      first_content: text.substring(0, 250),
    })

    try {
      const { id: docId } = await dox.createDocument(doxCreateRequest)
      window.location.assign(docId ? `${editorHomeUrl}/ddocs/${docId}` : editorHomeUrl)
    } catch (error) {
      window.location.assign(editorHomeUrl)
    }
  }

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    if (props.autofocus) {
      textAreaRef.current?.focus()
    }
  }, [props.autofocus])

  const isOverlayActive =
    textCheckStatus === 'LOADING_TEXT_CHECK' ||
    textCheckStatus === 'LOADING_FILE' ||
    textCheckStatus === 'NO_ALERTS_FOUND' ||
    textCheckStatus === 'TOO_MUCH_TEXT' ||
    textCheckStatus === 'FILE_ERROR_TOO_BIG' ||
    textCheckStatus === 'FILE_ERROR_INVALID_EXT' ||
    textCheckStatus === 'UNKNOWN_ERROR'
  const isNotEnoughTextAlertActive = textCheckStatus === 'NOT_ENOUGH_TEXT'

  const handleOnePageReportSuccess = (alerts: LensedAlert[]) => {
    setParsedTextCheckErrors(parseAlerts(alerts))
    setTextCheckStatus('ACTIVE')
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const toolsOptimalUsageLimitAssignment = getToolsOptimalUsageLimitAssignment(isAnonymous, slug)
    const isOptimalUsageLimitTestTreatment = isToolsOptimalUsageLimitTestTreatment(toolsOptimalUsageLimitAssignment)
    if (isOptimalUsageLimitTestTreatment) {
      plagiarismCheckerUsageTracker.setMaxToolUsage(
        getExpOptimalUsageLimit(toolsOptimalUsageLimitAssignment, maxSubmits)
      )
    }
    trackExperimentInfo(slug, experimentsWithAssignedTreatment)

    const trimmedUserText = trimUserText(userText, MAX_USER_INPUT_CHAR_COUNT)

    gnarSpec.checkTextSubmitButtonClick(userText.length, pageId, slug, calculateWordCount(userText))

    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
      .getCounter(CmsMetrics.ClientMetricsActions.Submit)
      .setContext({
        toolPage: CmsMetrics.ToolType.PlagiarismChecker,
      })
      .increment()

    if (wordCount < props.minimumWordCount) {
      void gnarSpec.checkTextNotEnoughTextPopupShow(pageId, slug, wordCount)
      setTextCheckStatus('NOT_ENOUGH_TEXT')
      return
    }

    if (isPremiumUser) {
      redirectUserToEditor(trimmedUserText)
      return
    }

    if (shouldHaveFastWall) {
      let countSubmit = Number(countSubmitString)
      let wallExpirationNum = Number(wallExpiration)
      const now = Date.now()
      const shouldResetCount =
        Number.isNaN(countSubmit) || countSubmit === 0 || Number.isNaN(wallExpiration) || now > wallExpirationNum

      if (shouldResetCount) {
        countSubmit = 0
        wallExpirationNum = now + expirationPeriod
      }

      const shouldTriggerWall =
        isOptimalUsageLimitTestTreatment || isToolsFreeUseLimitExperiment
          ? !plagiarismCheckerUsageTracker.checkToolUsageState().canUse
          : countSubmit >= maxSubmits
      if (shouldTriggerWall) {
        if (shouldHaveBouncyCtaWall && hasSeenWall) {
          if (!bounceCta) {
            setBounceCta(true)
            setTimeout(() => {
              setBounceCta(false)
            }, 2000)
          }
        } else {
          triggerUsageWall()
        }
        return
      }

      if (isOptimalUsageLimitTestTreatment || isToolsFreeUseLimitExperiment) {
        plagiarismCheckerUsageTracker.incrementToolUsageState()
      } else {
        countSubmit++
        setLocalStorageItem(LocalStorageKey.countPlagiarismSubmitAnonymous, String(countSubmit))
        setLocalStorageItem(LocalStorageKey.expirationPlagiarismSubmitAnonymous, String(wallExpirationNum))
      }
    }

    setTextCheckStatus('LOADING_TEXT_CHECK')

    try {
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
        .getCounter(CmsMetrics.ClientMetricsActions.Attempt)
        .setContext({
          toolPage: CmsMetrics.ToolType.PlagiarismChecker,
        })
        .increment()

      const response = await capi.checkText(trimmedUserText)
      void gnarSpec.checkTextFormFormSuccess(pageId, slug)
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
        .getCounter(CmsMetrics.ClientMetricsActions.Success)
        .setContext({
          toolPage: CmsMetrics.ToolType.PlagiarismChecker,
        })
        .increment()

      if (isOnePageReport) {
        handleOnePageReportSuccess(response)
      } else {
        if (response.length < 1) {
          setTextCheckStatus('NO_ALERTS_FOUND')
        } else {
          handleCheckTextSuccess(trimmedUserText, response)
        }
      }
    } catch (e) {
      // if this api call fails, we should show `NO_ALERTS_FOUND` popup with Product Install CTA
      setTextCheckStatus('UNKNOWN_ERROR')
      const error = transformToString(e as object)
      void gnarSpec.checkTextFormFormFail(pageId, slug, error)

      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
        .getCounter(CmsMetrics.ClientMetricsActions.Failure)
        .setContext({
          toolPage: CmsMetrics.ToolType.PlagiarismChecker,
        })
        .increment()
    }
  }

  const onActivate = () => {
    if (textCheckStatus !== 'LOADING_TEXT_CHECK' && textCheckStatus !== 'LOADING_FILE') {
      setTextCheckStatus('ACTIVE')
    }
    textAreaRef.current?.focus()
  }

  const onPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = event.clipboardData.getData('text')
    const selectedText = window.getSelection()?.toString() ?? ''
    const isOverLimit = userText.length - selectedText.length + pastedText.length > MAX_USER_INPUT_CHAR_COUNT
    gnarSpec.checkTextPastePaste(pastedText.length, pageId, slug, calculateWordCount(pastedText), isOverLimit)
  }

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserText(event.target.value)
    if (!hasInitialChangeRef.current) {
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
        .getCounter(CmsMetrics.ClientMetricsActions.Input)
        .setContext({
          toolPage: CmsMetrics.ToolType.PlagiarismChecker,
        })
        .increment()
      hasInitialChangeRef.current = true
    }
  }

  useEffect(() => {
    const wordCount = calculateWordCount(trimUserText(userText, MAX_USER_INPUT_CHAR_COUNT))
    setWordCount(wordCount)
  }, [userText])

  const characterCountText = intl.formatMessage(
    {
      id: 'textChecker.reportFlow.form.characterCount',
      description: 'Text Checker - Report Flow - Form - Character Count',
      defaultMessage: '{count}/{max}',
    },
    {
      count: userText.length,
      max: <strong>{MAX_USER_INPUT_CHAR_COUNT}</strong>,
    }
  )

  return (
    <div
      className={cn(styles.container, isOnePageReport && styles.onePageReportContainer)}
      data-qa="plagiarism-container"
    >
      <form className={styles.form} onSubmit={onSubmit} method="post">
        <div className={styles.formContent}>
          {isOverlayActive && (
            <TextCheckerOverlay
              status={textCheckStatus}
              onClose={onActivate}
              setStatus={setTextCheckStatus}
              setText={setUserText}
              scanForPlagiarism={props.scanForPlagiarism}
            />
          )}
          <div className={cn(styles.textAreaContainer, isOnePageReport && styles.onePageReportTextAreaContainer)}>
            {isNotEnoughTextAlertActive && (
              <TextCheckerNotEnoughTextAlert
                onUseSampleText={() => {
                  void gnarSpec.checkTextSampleTextLinkClick(pageId, slug)
                  setUserText(sampleText)
                }}
                onClose={onActivate}
              />
            )}
            <textarea
              ref={textAreaRef}
              className={cn(styles.textAreaInput, typographyStyles.C3)}
              name="userText"
              spellCheck={false}
              placeholder={
                props.placeholderText ||
                intl.formatMessage({
                  id: 'textChecker.reportFlow.form.textArea.placeholderText',
                  description: 'Text Checker - Report Flow - Form - Text Area - Placeholder Text',
                  defaultMessage: 'Start writing here.',
                })
              }
              value={userText}
              onChange={onChange}
              onFocus={onActivate}
              maxLength={MAX_USER_INPUT_CHAR_COUNT}
              onPaste={onPaste}
            />
          </div>
          <WordsLeft wordCount={wordCount} minWordCount={props.minimumWordCount} />
          <div className={styles.ctaContainer}>
            <div className={cn(typographyStyles.C5, styles.characterCountMobile)}>{characterCountText}</div>
            <ButtonCta
              className={styles.ctaButton}
              text={props.buttonCtaText ? renderRichTextSingleLineContent(props.buttonCtaText) : 'Check your text'}
              color={ButtonColor.PrimaryGreen}
              status={
                !hasMounted || textCheckStatus === 'LOADING_TEXT_CHECK' || textCheckStatus === 'LOADING_FILE'
                  ? ButtonStatus.Loading
                  : ButtonStatus.Active
              }
              type="submit"
              compact
            />
            {showUploadButton && (
              <UploadButton className={styles.ctaButton} setStatus={setTextCheckStatus} setText={setUserText} />
            )}
            <div className={cn(typographyStyles.C5, styles.characterCountDesktop)}>{characterCountText}</div>
          </div>
        </div>
      </form>
      {shouldHaveVoluntaryCta && !parsedTextCheckErrors && !isMobileOrTablet && (
        <HelperMessage
          ctaScaleUp={bounceCta}
          hasSeenWall={hasSeenWall}
          isForPlagiarismPlanPage={isForPlagiarismPlanPage}
        />
      )}
      {!!parsedTextCheckErrors && (
        <ReportSummary errors={parsedTextCheckErrors} isForPlagiarismPlanPage={isForPlagiarismPlanPage} />
      )}
      <Wall
        show={showWall}
        appElementForModal="#__next"
        close={() => setShowWall(false)}
        parentSelector='[data-qa="plagiarism-container"]'
        isForPlagiarismPlanPage={isForPlagiarismPlanPage}
        slug={slug}
        isMobileOrTablet={isMobileOrTablet}
      />
      <FreeUserTrialModal show={showTrialModal} close={() => setShowTrialModal(false)} />
    </div>
  )
}
